"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, X } from "lucide-react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

export function Hero() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <section className="relative min-h-[85vh] md:min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-honey-50 via-white to-ruby-50/30" />

      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />

      <div className="relative z-10 container mx-auto max-w-5xl text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-mocha-950/5 border border-mocha-950/10 mb-8">
          <span className="w-2 h-2 rounded-full bg-ruby-600 animate-pulse" />
          <span className="text-xs font-medium text-mocha-700 tracking-wide uppercase">
            Accesso anticipato aperto
          </span>
        </div>

        {/* Main Headline */}
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-mocha-950 leading-[1.1] mb-6">
          KennyDB
          <span className="block mt-2">
            Non è il solito{" "}
            <span className="text-ruby-600">gestionale</span>
          </span>
          <span className="block mt-2 text-mocha-600">
            per agenti di commercio.
          </span>
        </h1>

        {/* Subheadline */}
        <p className="font-sans text-lg md:text-xl text-mocha-600 leading-relaxed max-w-2xl mx-auto mb-10">
          Automatizza la burocrazia.{" "}
          <strong className="text-mocha-950">Vendi di più.</strong>{" "}
          Vivi meglio.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <Link href="/cta?type=trial&source=hero">
            <Button
              size="lg"
              className="bg-ruby-600 hover:bg-ruby-700 text-white px-8 py-6 text-lg font-semibold shadow-lg shadow-ruby-600/20 hover:shadow-ruby-600/30 transition-all duration-300"
            >
              Prova gratis 14 giorni
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <Button
            variant="outline"
            size="lg"
            className="border-mocha-200 text-mocha-700 hover:bg-mocha-50 px-8 py-6 text-lg"
            onClick={() => setDemoOpen(!demoOpen)}
          >
            {demoOpen ? (
              <X className="mr-2 h-5 w-5" />
            ) : (
              <Play className="mr-2 h-5 w-5" />
            )}
            {demoOpen ? "Chiudi" : "Guarda la demo"}
          </Button>
        </div>

        {/* Inline Demo Video */}
        <AnimatePresence>
          {demoOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0, marginBottom: 0 }}
              animate={{ opacity: 1, height: "auto", marginBottom: 32 }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="rounded-2xl overflow-hidden bg-mocha-950 shadow-2xl max-w-3xl mx-auto">
                <video
                  className="aspect-video w-full"
                  controls
                  autoPlay
                  src="/demo.mp4"
                >
                  Il tuo browser non supporta il tag video.
                </video>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-mocha-500">
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Nessuna carta richiesta
          </span>
          <span className="text-mocha-300">·</span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Setup in 15 minuti
          </span>
          <span className="text-mocha-300">·</span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Cancelli quando vuoi
          </span>
        </div>

        {/* Hero Showcase Image */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.8,
            delay: 0.3,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          className="mt-16 md:mt-20 w-full max-w-5xl mx-auto"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-mocha-950/10 ring-1 ring-mocha-200/50">
            {/* Glow effect behind image */}
            <div className="absolute inset-0 bg-gradient-to-t from-ruby-100/20 via-transparent to-honey-100/20 pointer-events-none z-10" />

            <Image
              src="/kenny-hero.jpeg"
              alt="KennyDB - Dashboard per agenti di commercio"
              width={1280}
              height={800}
              className="w-full h-auto"
              priority
            />

            {/* Subtle gradient overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white/80 to-transparent pointer-events-none z-10" />
          </div>

          {/* Caption */}
          <p className="text-center text-sm text-mocha-500 mt-4 italic">
            L'unica piattaforma progettata per il tuo lavoro
          </p>
        </motion.div>
      </div>

      {/* Decorative elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
}
